<template>
  <div class="recurrence-section-trigger">
    <Button
      type="button"
      class="button-link recurrence-toolbar-chip flex align-items-center gap-2"
      @click="toggle"
    >
      <Icon type="favicon" icon="Repeat" class="Icon" />
      <span class="text-sm font-medium">{{ triggerLabel }}</span>
      <i class="pi pi-chevron-down text-xs" />
    </Button>

    <OverlayPanel ref="panel" append-to="body" style="width: 340px" :breakpoints="{ '960px': '85vw' }">
      <div v-if="recurringId" class="recurrence-source-note mb-3">
        <Icon type="favicon" icon="Repeat" class="Icon" />
        <span class="text-sm">{{ $t("generatedFromRecurringSchedule") }}</span>
        <a href="#" class="text-sm ml-1" @click.prevent="viewSource">{{ $t("viewSource") }}</a>
      </div>

      <div v-if="schedules.length === 0" class="text-center">
        <p class="text-sm text-color-secondary mb-2">{{ $t("noRecurringScheduleSetUp") }}</p>
        <Button
          type="button"
          class="button-secondary p-button-sm w-full"
          @click="setUpSchedule"
        >
          {{ $t("setUpRecurringSchedule") }}
        </Button>
      </div>

      <div v-else>
        <div
          v-for="(schedule, index) in schedules"
          :key="schedule.id"
          class="recurrence-schedule-item"
          :class="{ 'recurrence-schedule-item-divider': index > 0 }"
        >
          <p class="text-sm mb-1">{{ getRecurrenceSummary(schedule) }}</p>
          <p v-if="nextOccurrence" class="text-sm text-color-secondary mb-2">
            {{ $t("nextOccurrence") }}: {{ formatDate(nextOccurrence) }}
          </p>
          <p v-else class="text-sm text-color-secondary mb-2">{{ $t("scheduleEnded") }}</p>
          <Button
            type="button"
            class="button-link p-0 text-sm"
            @click="editSchedule(schedule)"
          >
            {{ $t("edit") }}
          </Button>
        </div>
      </div>
    </OverlayPanel>
  </div>
</template>

<script>
import { RRule } from "rrule";
import { getMyGemService } from "@/services";
import { Core } from "@/helpers/core";

import { DOMAIN } from "../../services/domain-constant.js";

const SCHEDULAR_GEM_ID = DOMAIN.SCHEDULAR_GEM;

export default {
  name: "RecurrenceSection",

  props: {
    schedules: {
      type: Array,
      default: () => [],
    },
    nextOccurrence: {
      type: String,
      default: null,
    },
    recurringId: {
      type: Number,
      default: null,
    },
    domain: {
      type: Number,
      required: true,
    },
    objectId: {
      type: [Number, String],
      default: null,
    },
    recordLabel: {
      type: String,
      default: "",
    },
    recordDescription: {
      type: String,
      default: "",
    },
  },

  computed: {
    hasSchedule() {
      return this.schedules.length > 0;
    },

    triggerLabel() {
      if (this.recurringId) return this.$t("generated");
      if (this.nextOccurrence) return this.$t("next") + ": " + this.formatDate(this.nextOccurrence);
      if (this.hasSchedule) return this.$t("scheduleEnded");
      return this.$t("recurrence");
    },
  },

  methods: {
    toggle(event) {
      this.$refs.panel.toggle(event);
    },

    formatDate(dateStr) {
      return Core.datey(dateStr, "MMM D, YYYY");
    },

    getRecurrenceSummary(schedule) {
      if (!schedule.rrule) return this.$t("notRecurring");
      try {
        const options = RRule.parseString(schedule.rrule);
        if (schedule.starts_on) options.dtstart = new Date(schedule.starts_on);
        return new RRule(options).toText();
      } catch {
        return this.$t("customRecurrence");
      }
    },

    setUpSchedule() {
      this.$refs.panel.hide();
      this.emitter.emit("open-gem", {
        item: {
          key: "create",
          label: this.$t("setUpRecurringSchedule"),
          isVisible: true,
          service: getMyGemService(SCHEDULAR_GEM_ID),
          item: {
            display_name: this.recordLabel
              ? `Recurring ${this.recordLabel}`
              : "Recurring Schedule",
            description: this.recordDescription || "",
            domain: this.domain,
            object_id: this.objectId,
          },
        },
      });
    },

    editSchedule(schedule) {
      this.$refs.panel.hide();
      this.emitter.emit("open-gem", {
        item: {
          key: "edit",
          label: this.$t("editSchedule"),
          isVisible: true,
          service: getMyGemService(SCHEDULAR_GEM_ID),
          item: { id: schedule.id },
        },
      });
    },

    viewSource() {
      this.$refs.panel.hide();
      this.emitter.emit("open-gem", {
        item: {
          key: "edit",
          label: this.$t("edit"),
          isVisible: true,
          service: getMyGemService(this.domain),
          item: { id: this.recurringId },
        },
      });
    },
  },
};
</script>

<style scoped>
.recurrence-toolbar-chip {
  padding: 0.25rem 0.5rem;
}

.recurrence-source-note {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--p-surface-100);
  border-radius: 4px;
  padding: 0.5rem 0.6rem;
}

.recurrence-schedule-item-divider {
  border-top: 1px solid var(--p-surface-200);
  margin-top: 0.75rem;
  padding-top: 0.75rem;
}
</style>
